import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import { THEME_COLORS } from "../../context/constants";
import { CustomPhotoAvatar, HeygenVoice } from "../../context/types";
import HeygenVoicesDialog from "../../components/VoicePlayer/components/HeygenVoicesDialog";

interface EditAvatarDialogProps {
  open: boolean;
  avatar: CustomPhotoAvatar | null;
  voices: HeygenVoice[];
  voicesLoading: boolean;
  onClose: () => void;
  onSave: (payload: { name: string; default_voice_id: string | null }) => Promise<void>;
}

const EditAvatarDialog: React.FC<EditAvatarDialogProps> = ({
  open,
  avatar,
  voices,
  voicesLoading,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState("");
  const [voiceId, setVoiceId] = useState<string | null>(null);
  const [voiceMeta, setVoiceMeta] = useState<{ name: string } | null>(null);
  const [voicesDialogOpen, setVoicesDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open && avatar) {
      setName(avatar.name || "");
      setVoiceId(avatar.default_voice_id ?? null);
      setVoiceMeta(null);
    }
  }, [open, avatar]);

  // Resolve currently-selected voice's display name. Prefer the meta captured
  // when the user just picked one (covers custom voice-design voices that may
  // not appear in the HeyGen public voices list).
  const selectedVoiceName = useMemo(() => {
    if (voiceMeta?.name) return voiceMeta.name;
    if (!voiceId) return null;
    const match = voices.find((v) => v.voice_id === voiceId);
    return match?.name ?? null;
  }, [voiceId, voiceMeta, voices]);

  const handleSelectVoice = (
    selectedVoiceId: string,
    meta?: { name: string; preview_audio_url: string | null }
  ) => {
    setVoiceId(selectedVoiceId);
    if (meta?.name) setVoiceMeta({ name: meta.name });
    setVoicesDialogOpen(false);
  };

  const handleSave = async () => {
    if (!avatar) return;
    if (!name.trim()) return;
    try {
      setIsSaving(true);
      await onSave({ name: name.trim(), default_voice_id: voiceId });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            fontFamily: "Lexend, sans-serif",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Edit avatar
          <IconButton onClick={onClose} sx={{ marginLeft: "auto" }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            paddingTop: "8px !important",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#666",
                fontFamily: "Lexend, sans-serif",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: "4px",
              }}
            >
              Name
            </Typography>
            <TextField
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 100 }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontFamily: "Lexend, sans-serif",
                  borderRadius: "12px",
                  backgroundColor: "#fafafa",
                },
              }}
            />
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#666",
                fontFamily: "Lexend, sans-serif",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: "4px",
              }}
            >
              Default voice
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                startIcon={<RecordVoiceOverIcon />}
                onClick={() => setVoicesDialogOpen(true)}
                disabled={voicesLoading}
                sx={{
                  textTransform: "none",
                  fontFamily: "Lexend, sans-serif",
                  borderRadius: "10px",
                  borderColor: "#ddd",
                  color: "#333",
                }}
              >
                {voicesLoading ? "Loading voices..." : "Choose voice"}
              </Button>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: selectedVoiceName ? "#222" : "#999",
                  fontFamily: "Lexend, sans-serif",
                }}
              >
                {selectedVoiceName
                  ? selectedVoiceName
                  : voiceId
                  ? `Voice id ${voiceId.slice(0, 8)}...`
                  : "No default voice selected"}
              </Typography>
              {voiceId && (
                <Button
                  size="small"
                  onClick={() => {
                    setVoiceId(null);
                    setVoiceMeta(null);
                  }}
                  sx={{
                    textTransform: "none",
                    fontFamily: "Lexend, sans-serif",
                    color: "#999",
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "12px 24px 20px" }}>
          <Button
            onClick={onClose}
            sx={{
              textTransform: "none",
              fontFamily: "Lexend, sans-serif",
              color: "#666",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSaving || !name.trim()}
            sx={{
              backgroundColor: THEME_COLORS.yellow,
              color: "#000",
              fontFamily: "Lexend, sans-serif",
              fontWeight: 600,
              borderRadius: "10px",
              padding: "8px 20px",
              fontSize: "14px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: THEME_COLORS.darkYellow,
                boxShadow: "none",
              },
            }}
            startIcon={
              isSaving ? <CircularProgress size={16} sx={{ color: "#000" }} /> : undefined
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <HeygenVoicesDialog
        open={voicesDialogOpen}
        onClose={() => setVoicesDialogOpen(false)}
        heygenVoices={voices}
        onSelectVoice={handleSelectVoice}
        actorId={avatar?._id}
        actorName={avatar?.name}
      />
    </>
  );
};

export default EditAvatarDialog;
